// [claude-code 2026-05-15] S66-T3: @dnd-kit drag-and-drop toolbar wrapper.
// Replaces native HTML5 DnD with SortableContext + DragOverlay.
// PointerSensor at 5px distance prevents accidental drag on click.
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type ReactNode, useState } from "react";
import {
  setToolbarOrder,
  type ToolbarItemId,
} from "../../lib/layoutOrderStorage";

interface SortableToolbarItemProps {
  id: ToolbarItemId;
  children: ReactNode;
}

function SortableToolbarItem({ id, children }: SortableToolbarItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: "relative",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex touch-none select-none items-center gap-0.5 ${
        isDragging ? "cursor-grabbing" : "cursor-pointer"
      }`}
      title="Drag to reorder"
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

interface ToolbarDnDProps {
  items: ToolbarItemId[];
  children: (id: ToolbarItemId) => ReactNode;
  onOrderChange: (newOrder: ToolbarItemId[]) => void;
}

export function ToolbarDnD({
  items,
  children,
  onOrderChange,
}: ToolbarDnDProps) {
  const [activeId, setActiveId] = useState<ToolbarItemId | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as ToolbarItemId);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.indexOf(active.id as ToolbarItemId);
    const newIndex = items.indexOf(over.id as ToolbarItemId);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(items, oldIndex, newIndex);
    setToolbarOrder(newOrder);
    onOrderChange(newOrder);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={horizontalListSortingStrategy}>
        <div className="flex items-center gap-0.5">
          {items.map((id) => (
            <SortableToolbarItem key={id} id={id}>
              {children(id)}
            </SortableToolbarItem>
          ))}
        </div>
      </SortableContext>
      <DragOverlay>
        {activeId ? (
          <div className="flex items-center gap-1 rounded-md bg-[rgba(5,4,2,0.78)] px-3 py-1.5 backdrop-blur-sm">
            {children(activeId)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
